import { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'

const BUCKET_ENDPOINT_PREFIX = '/api/v5/jobs/buckets/'
const MAX_CACHE_SIZE = 50

interface ETagRequestConfig extends InternalAxiosRequestConfig {
  __cacheKey?: string
}

interface CachedEntry {
  etag: string
  data: unknown
}

const cache = new Map<string, CachedEntry>()
let requestEntries = new WeakMap<ETagRequestConfig, CachedEntry>()

function getCacheKey(config: ETagRequestConfig): string {
  const body = config.data ? JSON.stringify(config.data) : ''
  return `${config.url}::${body}`
}

function isBucketRequest(config: ETagRequestConfig): boolean {
  return config.method?.toLowerCase() === 'post' && !!config.url?.startsWith(BUCKET_ENDPOINT_PREFIX)
}

function evictOldest() {
  if (cache.size >= MAX_CACHE_SIZE) {
    const firstKey = cache.keys().next().value
    if (firstKey !== undefined) cache.delete(firstKey)
  }
}

export function clearETagCache() {
  cache.clear()
  requestEntries = new WeakMap()
}

export function setupETagInterceptor(instance: AxiosInstance) {
  instance.interceptors.request.use((config) => {
    const etagConfig = config as ETagRequestConfig

    if (isBucketRequest(etagConfig)) {
      const key = getCacheKey(etagConfig)
      etagConfig.__cacheKey = key
      const cached = cache.get(key)
      requestEntries.delete(etagConfig)
      delete config.headers['If-None-Match']
      if (cached) {
        config.headers['If-None-Match'] = cached.etag
        // A concurrent response may replace or evict the entry before this 304 returns.
        requestEntries.set(etagConfig, cached)
      }
    }
    return config
  })

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      const config = response.config as ETagRequestConfig

      if (isBucketRequest(config)) {
        const etag = response.headers.etag
        if (response.status === 200 && etag) {
          const key = config.__cacheKey || getCacheKey(config)
          if (!cache.has(key)) {
            evictOldest()
          }
          cache.set(key, { etag, data: response.data })
        }
      }
      return response
    },
    (error) => {
      const config = error.config as ETagRequestConfig | undefined

      if (error.response?.status === 304 && config && isBucketRequest(config)) {
        const cached = requestEntries.get(config)
        if (cached) {
          return {
            ...error.response,
            status: 200,
            statusText: 'OK',
            data: cached.data,
          } as AxiosResponse
        }
      }
      return Promise.reject(error)
    },
  )
}
